import React, { useRef, useState } from "react";
import { X } from "lucide-react";
import { cn } from "../../../../lib/utils";
import { WhatsAppIcon } from "./WhatsAppIcon";

export const SupportButton = () => {
  const [isDismissed, setIsDismissed] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [windowSize, setWindowSize] = useState({ width: typeof window !== 'undefined' ? window.innerWidth : 0, height: typeof window !== 'undefined' ? window.innerHeight : 0 });
  
  const dragStateRef = useRef(null);

  React.useEffect(() => {
    const handleResize = () => setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleChat = () => setIsOpen(!isOpen);

  React.useEffect(() => {
    const handlePointerMove = (event) => {
      const dragState = dragStateRef.current;
      if (!dragState) return;

      const deltaX = event.clientX - dragState.startX;
      const deltaY = event.clientY - dragState.startY;
      const hasMovedEnough = Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5;

      if (!hasMovedEnough) return;

      setIsDragging(true);
      const nextX = dragState.originX + deltaX;
      const nextY = dragState.originY + deltaY;
      const maxX = Math.max(0, window.innerWidth - dragState.width);
      const maxY = Math.max(0, window.innerHeight - dragState.height);

      setPosition({
        x: Math.min(Math.max(0, nextX), maxX),
        y: Math.min(Math.max(0, nextY), maxY),
      });
    };

    const handlePointerUp = () => {
      const dragState = dragStateRef.current;
      if (!dragState) return;

      setTimeout(() => {
        setIsDragging(false);
      }, 50);
      
      dragStateRef.current = null;
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
    };
  }, []);

  const handleDismiss = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setIsOpen(false);
    setIsDismissed(true);
  };

  const handlePointerDown = (event) => {
    if (event.button !== undefined && event.button !== 0) return;

    if (event.target.closest('[data-support-dismiss]')) return;

    // Don't drag if clicking inside the chat window
    if (event.target.closest('.chat-window-content')) return;

    const target = event.currentTarget;
    const rect = target.getBoundingClientRect();

    dragStateRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      originX: rect.left,
      originY: rect.top,
      width: rect.width,
      height: rect.height,
    };
  };

  const handleToggleClick = (event) => {
    if (isDragging) return;
    event?.stopPropagation();
    toggleChat();
  };

  const popupOriginClass = (() => {
    if (!position || windowSize.width === 0) return "origin-bottom-right bottom-full mb-4 right-0";
    
    const centerX = position.x + 28;
    const centerY = position.y + 28;
    
    const isLeft = centerX < windowSize.width / 2;
    const isTop = centerY < windowSize.height / 2;
    
    let cls = "";
    if (isTop) cls += "top-[calc(100%+16px)] origin-top";
    else cls += "bottom-[calc(100%+16px)] origin-bottom";
    
    if (isLeft) cls += "-left left-0";
    else cls += "-right right-0";
    
    return cls;
  })();

  const bannerPositionClasses = (() => {
    if (!position || windowSize.width === 0) return "right-2 bottom-[calc(100%+10px)]";
    
    let cls = "";
    // If too close to left, snap to left side. Otherwise right side.
    if (position.x < 80) cls += "left-2 ";
    else cls += "right-2 ";

    // If too close to top, snap to below the button. Otherwise above it.
    if (position.y < 60) cls += "top-[calc(100%+10px)]";
    else cls += "bottom-[calc(100%+10px)]";

    return cls;
  })();

  if (isDismissed) return null;

  return (
    <div
      className={cn(
        "fixed z-[9999] flex flex-col items-end gap-4",
        position ? "cursor-grabbing" : "bottom-6 right-6 cursor-grab"
      )}
      style={position ? { left: position.x, top: position.y } : undefined}
      onPointerDown={handlePointerDown}
    >
      {/* Chat Window */}
      {isOpen && (
        <div className={cn("absolute chat-window-content w-[300px] rounded-[16px] overflow-hidden shadow-[0_8px_40px_rgba(0,0,0,0.22)] font-['Inter',sans-serif] animate-in zoom-in-95 duration-200 cursor-auto", popupOriginClass)}>
          <div className="bg-[#0f1318] px-4 py-3.5 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#0a3891] flex items-center justify-center shrink-0">
              <span className="text-white font-extrabold text-base">P</span>
            </div>
            <div className="flex-1">
              <p className="text-white font-bold text-sm m-0">Prodyt</p>
              <p className="text-[#22c55e] text-[11px] m-0 flex items-center gap-1">
                <span className="w-[7px] h-[7px] rounded-full bg-[#22c55e] inline-block"></span>
                Typically replies in minutes
              </p>
            </div>
            <button onClick={toggleChat} className="bg-transparent border-none cursor-pointer text-white/50 text-[18px] leading-none p-0.5 hover:text-white transition-colors">×</button>
          </div>
          <div className="bg-[#ece5dd] px-3.5 pt-4 pb-3">
            <div className="bg-white rounded-[0_10px_10px_10px] px-3.5 py-2.5 mb-3 shadow-[0_1px_2px_rgba(0,0,0,0.1)]">
              <p className="text-[11px] text-[#888888] m-0 mb-1 font-semibold">Prodyt</p>
              <p className="text-[13.5px] text-[#111111] leading-[1.55] m-0">
                Hi! Interested in Prodyt?<br/>Click below and we will get back to you right away.
              </p>
            </div>
            <a 
              href="https://api.whatsapp.com/send/?phone=919650169531&text=Hi%21+I%27m+interested+in+Prodyt+Connect.+Can+you+help+me+get+started%3F&type=phone_number&app_absent=0" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="flex items-center justify-center gap-2 bg-[#25d366] hover:bg-[#20bd5a] transition-colors text-white rounded-[10px] py-[13px] px-4 no-underline font-bold text-sm shadow-sm"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"></path>
                <path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.126 1.532 5.859L.054 23.447a.5.5 0 0 0 .609.61l5.684-1.49A11.945 11.945 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.808 9.808 0 0 1-5.032-1.388l-.36-.214-3.733.979.993-3.63-.235-.374A9.808 9.808 0 0 1 2.182 12C2.182 6.57 6.57 2.182 12 2.182S21.818 6.57 21.818 12 17.43 21.818 12 21.818z"></path>
              </svg>
              Chat on WhatsApp
            </a>
          </div>
        </div>
      )}
      <div className="relative flex items-center justify-center w-14 h-14">
        <button
          type="button"
          data-support-dismiss
          aria-label="Dismiss support button"
          className="absolute -top-1.5 -right-1.5 z-10 flex h-5 w-5 items-center justify-center rounded-full border border-white/20 bg-gray-800 text-white shadow-md transition-colors hover:bg-gray-900 dark:border-slate-600 dark:bg-slate-700 dark:hover:bg-slate-600"
          onClick={handleDismiss}
          onPointerDown={(event) => event.stopPropagation()}
        >
          <X className="h-3 w-3" aria-hidden />
        </button>

        {/* Need Help Banner */}
        {!isOpen && (
          <div 
            onClick={handleToggleClick}
            className={cn("absolute bg-white dark:bg-gray-800 px-4 py-2 rounded-full shadow-[0_4px_12px_rgba(0,0,0,0.08)] border border-gray-100 dark:border-gray-700 animate-in fade-in duration-500 select-none whitespace-nowrap cursor-pointer transition-all", bannerPositionClasses)}
          >
            <p className="text-[14px] font-bold text-gray-800 dark:text-gray-200 m-0">Need help?</p>
          </div>
        )}

        {/* Toggle Button */}
        <button 
          type="button"
          aria-label="Open WhatsApp support chat"
          className={cn(
            "h-14 w-14 rounded-full flex items-center justify-center text-white shadow-xl transition-all duration-300 active:scale-95 select-none touch-none",
            isOpen ? "bg-[#22c55e] rotate-90" : "bg-[#22c55e] hover:scale-110"
          )}
          onClick={handleToggleClick}
        >
          {isOpen ? (
            <X className="h-7 w-7" />
          ) : (
            <WhatsAppIcon className="w-8 h-8 fill-current" />
          )}
        </button>
      </div>
    </div>
  );
};
